import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import PaymentFlow from './services/paymentFlow';
import ServiceChargeModal from './components/ServiceChargeModal';
import ProcessingModal from './components/ProcessingModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import ServicePricingBadge from './components/ServicePricingBadge';
import PaymentValidationModal from './components/PaymentValidationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lucide React Icons
import { FileText, Upload, Download, Printer, Mail, Check } from 'lucide-react';

// UI Components
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow ${className}`}>{children}</div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`p-6 ${className}`}>{children}</div>
);

const CardTitle = ({ children, className = '' }) => (
  <h2 className={`text-xl font-semibold ${className}`}>{children}</h2>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`p-6 pt-0 ${className}`}>{children}</div>
);

const Button = ({ children, onClick, disabled, className = '', type = 'button', variant = 'default' }) => {
  const baseClasses = 'px-6 py-3 rounded-lg font-semibold transition-all';
  const variantClasses = variant === 'outline' 
    ? 'border-2 border-slate-300 hover:bg-slate-50' 
    : 'bg-blue-600 hover:bg-blue-700 text-white';
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variantClasses} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-sm font-semibold text-slate-700 mb-2">
    {children}
  </label>
);

const Input = ({ ...props }) => (
  <input
    {...props}
    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  />
);

const Textarea = ({ ...props }) => (
  <textarea
    {...props}
    className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
  />
);

const AKSWPage = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [pricingInfo, setPricingInfo] = useState(null);
  
  // Payment flow states
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [showPaymentValidation, setShowPaymentValidation] = useState(false);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [chargeInfo, setChargeInfo] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [serviceAccessDenied, setServiceAccessDenied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progressSteps, setProgressSteps] = useState([]);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  const [lastCharge, setLastCharge] = useState(null);
  const [error, setError] = useState('');
  
  // Dashboard metrics state
  const [reports, setReports] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  const [formData, setFormData] = useState({
    book_type: 'ebook', // ebook, paperback, hardcover, both
    book_title: '',
    subtitle: '',
    author_name: '',
    isbn: '',
    publisher_name: '',
    author_bio: '',
    book_price: '',
    trim_size: '6x9', // for print books
    interior_type: 'bw', // bw, standard_color, premium_color
    manuscript_file: null
  });
  
  const [manuscriptContent, setManuscriptContent] = useState('');

  useEffect(() => {
    // Validate service access on mount
    validateServiceAccess();
    
    // Load specific report if reportId passed via navigation state
    if (location.state?.reportId) {
      loadReport(location.state.reportId);
    }
  }, []);

  const loadReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/aksw/reports/${reportId}`, { withCredentials: true });
      setReport(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Failed to load report');
    }
  };

  useEffect(() => {
    // Determine pricing based on user role
    const role = user?.role || 'free_user';
    const subscriptionStatus = user?.subscription_status || '';
    
    let pricing = {};
    if (role === 'admin' || role === 'beta_tester') {
      pricing = {
        price: 0,
        message: 'Unlimited Free Access - Included with your account',
        payment_required: false
      };
    } else if (role === 'subscriber' || ['active', 'monthly', 'annual'].includes(subscriptionStatus.toLowerCase())) {
      pricing = {
        price: 0,
        message: 'Unlimited Free Access - Included with your subscription',
        payment_required: false
      };
    } else {
      pricing = {
        price: 3.50,
        message: 'Standard Service - $5.00 per use',
        payment_required: true
      };
    }
    
    setPricingInfo(pricing);
  }, [user]);

  // Fetch dashboard metrics
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch all AKSW reports
        const reportsResponse = await axios.get(`${API}/aksw/reports`, {
          withCredentials: true
        });
        setReports(reportsResponse.data || []);

        // Fetch transaction history
        const transactionsResponse = await axios.get(`${API}/payments/transactions`, {
          withCredentials: true
        });
        setTransactions(transactionsResponse.data.transactions || []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const validateServiceAccess = async () => {
    try {
      const verification = await PaymentFlow.verifyPaymentForService('aksw');
      
      if (verification.access_granted) {
        if (verification.discount_info) {
          setChargeInfo(verification.discount_info);
        }
        setPaymentCompleted(true);
        return;
      }
      
      if (verification.requires_payment_validation) {
        setShowPaymentValidation(true);
        setServiceAccessDenied(true);
        return;
      }
      
      setServiceAccessDenied(true);
    } catch (error) {
      console.error('Service validation error:', error);
      setServiceAccessDenied(true);
    }
  };

  const handlePaymentValidationSuccess = (result) => {
    const roleName = result.user_role ? result.user_role.replace('_', ' ') : 'A la Carte';
    toast.success(`Payment method added! You are now an ${roleName} user.`);
    setShowPaymentValidation(false);
    setServiceAccessDenied(false);
    setPaymentCompleted(true);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setFormData(prev => ({ ...prev, manuscript_file: file }));
    
    // Extract text from file
    try {
      const text = await extractTextFromFile(file);
      setManuscriptContent(text);
      toast.success('Manuscript uploaded successfully');
    } catch (error) {
      toast.error('Failed to read manuscript file');
      console.error(error);
    }
  };

  const extractTextFromFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target.result;
        
        if (file.name.endsWith('.txt')) {
          resolve(content);
        } else if (file.name.endsWith('.pdf')) {
          // Allow PDFs - backend will validate if content is readable
          resolve(content.substring(0, 50000));
        } else if (file.name.endsWith('.docx') || file.name.endsWith('.doc')) {
          // For Word docs, use simple text extraction
          resolve(content.substring(0, 50000));
        } else {
          resolve(content.substring(0, 50000));
        }
      };
      
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!manuscriptContent) {
      toast.error('Please upload a manuscript file');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const submissionData = {
      name: 'Amazon KDP Submission Wizard',
      bookTitle: formData.book_title || 'AKSW Generation'
    };
    
    try {
      // Check if user needs to pay BEFORE processing (only for À La Carte users)
      if (user?.role === 'a_la_carte') {
        // Show payment authorization modal BEFORE any processing or charging
        setPaymentInfo({
          amount: 5.00,  // Standard service - $5 for a la carte
          currency: 'USD',
          description: 'Author/Kindle Store Website - AI-powered KDP metadata optimization',
          submission_data: submissionData,
          name: 'Author/Kindle Store Website',
          bookTitle: formData.book_title || 'AKSW Generation'
        });
        setShowPaymentModal(true);
        setLoading(false);
        return; // Stop here - wait for user to confirm payment
      }
      
      // Process the generation (for subscribers, admins, beta testers who don't need payment confirmation)
      await processGeneration();
      
    } catch (err) {
      console.error('AKSW submission error:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred while generating KDP wizard.');
      setLoading(false);
    }
  };

  const processGeneration = async () => {
    setIsProcessing(true);
    setProcessingProgress(5);
    
    // Initialize 7 progress steps for streamlined AKSW process
    const initialSteps = [
      { text: 'Analyzing manuscript', completed: false, current: true },
      { text: 'Extracting book intelligence', completed: false, current: false },
      { text: 'Generating categories and keywords', completed: false, current: false },
      { text: 'Creating descriptions and copy', completed: false, current: false },
      { text: 'Formatting requirements and specs', completed: false, current: false },
      { text: 'Finalizing pricing and distribution', completed: false, current: false },
      { text: 'Compiling submission guide', completed: false, current: false }
    ];
    setProgressSteps(initialSteps);
    setCurrentStep('Submitting job for processing...');
    
    try {
      // STEP 1: Submit async job
      const submitResponse = await axios.post(
        `${API}/aksw/submit-async`,
        {
          book_type: formData.book_type,
          book_title: formData.book_title,
          subtitle: formData.subtitle || null,
          author_bio: formData.author_bio,
          isbn: formData.isbn,
          publisher_name: formData.publisher_name,
          book_price: parseFloat(formData.book_price),
          manuscript_content: manuscriptContent
        },
        { 
          withCredentials: true,
          timeout: 30000 // 30 seconds - just for job submission
        }
      );
      
      if (!submitResponse.data.success) {
        throw new Error(submitResponse.data.message || 'Failed to start job');
      }
      
      const jobId = submitResponse.data.job_id;
      setCurrentStep('Job submitted. Processing in background...');
      setProcessingProgress(10);
      
      // STEP 2: Poll for job status
      let pollCount = 0;
      const maxPolls = 120; // 10 minutes max (5 sec intervals)
      
      const pollJob = async () => {
        try {
          const statusResponse = await axios.get(
            `${API}/jobs/${jobId}`,
            { withCredentials: true }
          );
          
          const job = statusResponse.data;
          
          // Update progress from backend
          setProcessingProgress(job.progress);
          
          // Update step display based on progress
          if (job.progress >= 10 && job.progress < 30) {
            setCurrentStep('Extracting book intelligence...');
            updateStepProgress(1);
          } else if (job.progress >= 30 && job.progress < 50) {
            setCurrentStep('Parsing manuscript insights...');
            updateStepProgress(2);
          } else if (job.progress >= 50 && job.progress < 80) {
            setCurrentStep('Generating KDP sections...');
            updateStepProgress(4);
          } else if (job.progress >= 80 && job.progress < 90) {
            setCurrentStep('Parsing sections...');
            updateStepProgress(5);
          } else if (job.progress >= 90 && job.progress < 100) {
            setCurrentStep('Saving results...');
            updateStepProgress(6);
          }
          
          // Check job status
          if (job.status === 'completed') {
            setProcessingProgress(100);
            setCurrentStep('Complete!');
            setProgressSteps(prev => prev.map(step => ({ ...step, completed: true, current: false })));
            
            // Fetch the actual report
            const reportResponse = await axios.get(
              `${API}/aksw/reports/${job.result.report_id}`,
              { withCredentials: true }
            );
            
            setReport(reportResponse.data);
            
            // Show email receipt modal if there was a transaction
            if (reportResponse.data.transaction_id) {
              setLastTransactionId(reportResponse.data.transaction_id);
              setShowEmailReceiptModal(true);
            }
            
            toast.success('KDP Submission Wizard completed successfully!');
            window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
            
            setIsProcessing(false);
            setLoading(false);
            return; // Done
            
          } else if (job.status === 'failed') {
            throw new Error(job.error || 'Job processing failed');
            
          } else if (job.status === 'processing' || job.status === 'pending') {
            // Continue polling
            pollCount++;
            if (pollCount < maxPolls) {
              setTimeout(pollJob, 5000); // Poll every 5 seconds
            } else {
              throw new Error('Job timeout - processing took too long');
            }
          }
          
        } catch (pollError) {
          if (pollError.response?.status === 404) {
            throw new Error('Job not found');
          }
          throw pollError;
        }
      };
      
      // Start polling after 2 seconds
      setTimeout(pollJob, 2000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'An error occurred');
      setIsProcessing(false);
      setLoading(false);
      setProgressSteps([]);
    }
  };
  
  // Helper to update progress steps
  const updateStepProgress = (stepIndex) => {
    setProgressSteps(prev => {
      const updated = [...prev];
      for (let i = 0; i <= stepIndex; i++) {
        updated[i].completed = true;
        updated[i].current = false;
      }
      if (stepIndex + 1 < updated.length) {
        updated[stepIndex + 1].current = true;
      }
      return updated;
    });
  };

  const handlePaymentSuccess = async (paymentData) => {
    setShowPaymentModal(false);
    setLoading(true);
    
    try {
      // IMPORTANT: User confirmed willingness to pay
      // BUT actual charge happens in backend AFTER successful generation
      // This ensures users are only charged for successfully completed services
      
      await processGeneration();
      
      // Note: Backend will create transaction record after successful completion
      // Transaction ID will be in the response from processGeneration
      
    } catch (error) {
      console.error('Post-payment generation error:', error);
      setError(error.response?.data?.detail || error.message || 'Generation failed. You have NOT been charged.');
      setLoading(false);
    }
  };

  const handleNewReport = () => {
    setReport(null);
    setFormData({
      book_title: '',
      subtitle: '',
      isbn: '',
      publisher_name: '',
      author_bio: '',
      book_price: '',
      manuscript_file: null
    });
    setManuscriptContent('');
  };

  const printResults = () => {
    if (!report) return;
    
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      const timestamp = new Date().toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Amazon KDP Submission Wizard - ${report.book_title}</title>
            <style>
              body { font-family: 'Times New Roman', Times, serif; font-size: 12pt; margin: 15px; line-height: 1.5; }
              .print-header { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; color: #666; }
              .report-header { text-align: center; margin-bottom: 20px; }
              .report-header img { height: 80px; margin: 0 auto 10px; display: block; }
              .report-header h1 { font-size: 18pt; margin: 8px 0; color: #1e40af; }
              .report-header h2 { font-size: 16pt; margin: 5px 0; }
              .book-info { margin: 15px 0; padding: 12px; background-color: #f8fafc; border: 1px solid #ddd; border-radius: 6px; }
              .book-info p { margin: 5px 0; font-size: 11pt; }
              .section { margin-bottom: 15px; padding: 12px; border-left: 4px solid; border-radius: 6px; page-break-inside: avoid; }
              .section h3 { font-size: 14pt; font-weight: bold; margin: 0 0 8px 0; }
              .section ul { margin: 8px 0; padding-left: 20px; }
              .section li { margin: 4px 0; }
              .footer { margin-top: 20px; text-align: center; font-size: 10pt; color: #666; border-top: 1px solid #ddd; padding-top: 12px; }
              @media print { body { margin: 10px; } }
            </style>
          </head>
          <body>
            <div class="print-header">
              <div>Generated: ${timestamp}</div>
              <div>Report ID: ${report.report_id}</div>
            </div>
            
            <div class="report-header">
              <img src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" alt="Digital Ink AI" />
              <h1>Amazon KDP Submission Wizard</h1>
              <h2>${report.book_title}</h2>
              ${report.subtitle ? `<p style="color: #666; font-size: 13pt;">${report.subtitle}</p>` : ''}
              ${report.author_name ? `<p style="color: #666; font-size: 11pt; margin-top: 5px;">by ${report.author_name}</p>` : ''}
            </div>
            
            <div class="book-info">
              <p><strong>Author Name:</strong> ${report.author_name || 'N/A'}</p>
              <p><strong>ISBN:</strong> ${formData.isbn}</p>
              <p><strong>Publisher:</strong> ${formData.publisher_name}</p>
              <p><strong>Price:</strong> $${formData.book_price}</p>
            </div>
            
            <div class="section" style="border-left-color: #3b82f6; background-color: #eff6ff;">
              <h3>📚 KDP Categories</h3>
              <ul>
                ${report.kdp_categories?.map(cat => `<li>${cat}</li>`).join('') || '<li>No categories</li>'}
              </ul>
            </div>
            
            <div class="section" style="border-left-color: #8b5cf6; background-color: #f5f3ff;">
              <h3>🔑 KDP Keywords</h3>
              <ul>
                ${report.kdp_keywords?.map(kw => `<li>${kw}</li>`).join('') || '<li>No keywords</li>'}
              </ul>
            </div>
            
            <div class="section" style="border-left-color: #10b981; background-color: #d1fae5;">
              <h3>📝 Book Description</h3>
              <p>${report.book_description || 'No description'}</p>
            </div>
            
            <div class="section" style="border-left-color: #f59e0b; background-color: #fef3c7;">
              <h3>👤 Author Bio (Optimized)</h3>
              <p>${report.author_bio_optimized || 'No bio'}</p>
            </div>
            
            <div class="section" style="border-left-color: #ec4899; background-color: #fce7f3;">
              <h3>📖 Back Cover Copy</h3>
              <p>${report.back_cover_copy || 'No copy'}</p>
            </div>
            
            <div class="section" style="border-left-color: #06b6d4; background-color: #cffafe;">
              <h3>🔍 Amazon Search Terms</h3>
              <ul>
                ${report.search_terms?.map(term => `<li>${term}</li>`).join('') || '<li>No terms</li>'}
              </ul>
            </div>
            
            <div class="section" style="border-left-color: #6366f1; background-color: #e0e7ff;">
              <h3>🎯 Age Range & Grade Level</h3>
              <p><strong>Age Range:</strong> ${report.age_range || 'N/A'}</p>
              <p><strong>Grade Level:</strong> ${report.grade_level || 'N/A'}</p>
            </div>
            
            <div class="section" style="border-left-color: #84cc16; background-color: #ecfccb;">
              <h3>📊 BISAC Categories</h3>
              <ul>
                ${report.bisac_categories?.map(cat => `<li>${cat}</li>`).join('') || '<li>No categories</li>'}
              </ul>
            </div>
            
            <div class="section" style="border-left-color: #ef4444; background-color: #fee2e2;">
              <h3>🎬 Marketing Copy</h3>
              <p>${report.marketing_copy || 'No copy'}</p>
            </div>
            
            <div class="footer">
              <p>Generated by Digital Ink AI - Amazon KDP Submission Wizard</p>
              <p>© ${new Date().getFullYear()} Digital Ink AI. All rights reserved.</p>
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 250);
    }
  };

  const exportToPDF = () => {
    if (!report) return;
    
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPos = 20;
    
    // Header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Amazon KDP Submission Wizard', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 10;
    pdf.setFontSize(14);
    pdf.text(report.book_title, pageWidth / 2, yPos, { align: 'center' });
    
    if (report.subtitle) {
      yPos += 8;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'italic');
      pdf.text(report.subtitle, pageWidth / 2, yPos, { align: 'center' });
      pdf.setFont('helvetica', 'normal');
    }
    
    if (report.author_name) {
      yPos += 8;
      pdf.setFontSize(11);
      pdf.text(`by ${report.author_name}`, pageWidth / 2, yPos, { align: 'center' });
    }
    
    yPos += 15;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    
    // Book Info
    pdf.text(`Author Name: ${report.author_name || 'N/A'}`, margin, yPos);
    yPos += 6;
    pdf.text(`ISBN: ${formData.isbn}`, margin, yPos);
    yPos += 6;
    pdf.text(`Publisher: ${formData.publisher_name}`, margin, yPos);
    yPos += 6;
    pdf.text(`Price: $${formData.book_price}`, margin, yPos);
    yPos += 10;
    
    // Sections
    const sections = [
      { title: 'KDP Categories', content: report.kdp_categories?.join('\n• ') || 'None', prefix: '• ' },
      { title: 'KDP Keywords', content: report.kdp_keywords?.join('\n• ') || 'None', prefix: '• ' },
      { title: 'Book Description', content: report.book_description || 'None' },
      { title: 'Author Bio (Optimized)', content: report.author_bio_optimized || 'None' },
      { title: 'Back Cover Copy', content: report.back_cover_copy || 'None' },
      { title: 'Amazon Search Terms', content: report.search_terms?.join('\n• ') || 'None', prefix: '• ' },
      { title: 'Age Range & Grade Level', content: `Age Range: ${report.age_range}\nGrade Level: ${report.grade_level}` },
      { title: 'BISAC Categories', content: report.bisac_categories?.join('\n• ') || 'None', prefix: '• ' },
      { title: 'Marketing Copy', content: report.marketing_copy || 'None' }
    ];
    
    sections.forEach((section, index) => {
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text(section.title, margin, yPos);
      yPos += 7;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const lines = pdf.splitTextToSize(section.content, maxWidth);
      lines.forEach(line => {
        if (yPos > 270) {
          pdf.addPage();
          yPos = 20;
        }
        pdf.text(line, margin, yPos);
        yPos += 5;
      });
      
      yPos += 5;
    });
    
    pdf.save(`AKSW-${report.book_title.replace(/[^a-z0-9]/gi, '_')}-${Date.now()}.pdf`);
    toast.success('PDF exported successfully!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      {!report ? (
        // Form Page
        <>
          {/* Header with Logo and Navigation */}
          <header className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <img 
                    src="/Digital_Ink_AI_Logo.png" 
                    alt="Digital Ink AI Logo" 
                    className="h-12 w-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                    }}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => navigate('/homepage')}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    🏠 Home
                  </button>
                  <button
                    onClick={() => navigate('/user_dashboard')}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    📊 Dashboard
                  </button>
                </div>
              </div>
            </div>
          </header>

          <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Service Title and Description */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">
              📦 Amazon KDP Submission Wizard
            </h1>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Transform manuscripts into Amazon KDP success. AI generates optimized book descriptions, strategic keywords, targeted categories, compelling back cover copy, and enhanced author bios. Maximize discoverability and conversions on the world's largest book marketplace with professionally crafted metadata designed for maximum sales impact.
            </p>
          </div>

          {/* Pricing Info */}
          {user && (
            <div className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-6">
              <div className="text-center">
                {user.role === 'subscriber' || user.role === 'admin' || user.role === 'beta_tester' ? (
                  <div>
                    <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                    <p className="text-green-700">Included with your subscription - optimize as many books as you need!</p>
                  </div>
                ) : (
                  <div>
                    <h3 className="text-xl font-bold text-blue-900 mb-2">💎 Standard Service - $5.00</h3>
                    <p className="text-blue-700">Complete KDP optimization package charged to your payment method on file</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="text-center mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-10 mx-auto mb-3"
                    />
                    <CardTitle className="text-2xl text-slate-900">📚 Book Information</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Book Type Selection */}
                    <div>
                      <Label htmlFor="book_type">Book Type *</Label>
                      <select
                        id="book_type"
                        value={formData.book_type}
                        onChange={(e) => setFormData(prev => ({ ...prev, book_type: e.target.value }))}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="ebook">Kindle eBook</option>
                        <option value="paperback">Paperback</option>
                        <option value="hardcover">Hardcover</option>
                        <option value="both">eBook + Print (Both)</option>
                      </select>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="book_title">Book Title *</Label>
                        <Input
                          id="book_title"
                          value={formData.book_title}
                          onChange={(e) => setFormData(prev => ({ ...prev, book_title: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="subtitle">Subtitle</Label>
                        <Input
                          id="subtitle"
                          value={formData.subtitle}
                          onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="author_name">Author Name *</Label>
                      <Input
                        id="author_name"
                        value={formData.author_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                        required
                        placeholder="e.g., John Smith"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="isbn">ISBN *</Label>
                        <Input
                          id="isbn"
                          value={formData.isbn}
                          onChange={(e) => setFormData(prev => ({ ...prev, isbn: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="book_price">Book Price (USD) *</Label>
                        <Input
                          id="book_price"
                          type="number"
                          step="0.01"
                          value={formData.book_price}
                          onChange={(e) => setFormData(prev => ({ ...prev, book_price: e.target.value }))}
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="publisher_name">Publisher Name *</Label>
                      <Input
                        id="publisher_name"
                        value={formData.publisher_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, publisher_name: e.target.value }))}
                        required
                      />
                    </div>

                    {/* Print Book Specifications - Show only for print books */}
                    {(formData.book_type === 'paperback' || formData.book_type === 'hardcover' || formData.book_type === 'both') && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="md:col-span-2">
                          <h4 className="font-semibold text-blue-900">Print Book Specifications</h4>
                        </div>
                        <div>
                          <Label htmlFor="trim_size">Trim Size *</Label>
                          <select
                            id="trim_size"
                            value={formData.trim_size}
                            onChange={(e) => setFormData(prev => ({ ...prev, trim_size: e.target.value }))}
                            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg"
                            required
                          >
                            <option value="5x8">5" x 8"</option>
                            <option value="5.25x8">5.25" x 8"</option>
                            <option value="5.5x8.5">5.5" x 8.5"</option>
                            <option value="6x9">6" x 9" (Most Popular)</option>
                            <option value="6.14x9.21">6.14" x 9.21"</option>
                            <option value="6.69x9.61">6.69" x 9.61"</option>
                            <option value="7x10">7" x 10"</option>
                            <option value="7.5x9.25">7.5" x 9.25"</option>
                            <option value="8x10">8" x 10"</option>
                            <option value="8.25x11">8.25" x 11"</option>
                            <option value="8.5x11">8.5" x 11"</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="interior_type">Interior Type *</Label>
                          <select
                            id="interior_type"
                            value={formData.interior_type}
                            onChange={(e) => setFormData(prev => ({ ...prev, interior_type: e.target.value }))}
                            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg"
                            required
                          >
                            <option value="bw">Black & White</option>
                            <option value="standard_color">Standard Color</option>
                            <option value="premium_color">Premium Color</option>
                          </select>
                        </div>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="author_bio">Author Bio *</Label>
                      <Textarea
                        id="author_bio"
                        rows={4}
                        value={formData.author_bio}
                        onChange={(e) => setFormData(prev => ({ ...prev, author_bio: e.target.value }))}
                        placeholder="Tell us about yourself..."
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="manuscript">Manuscript Upload *</Label>
                      <input
                        id="manuscript"
                        type="file"
                        accept=".txt,.doc,.docx,.pdf"
                        onChange={handleFileUpload}
                        className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg"
                        required
                      />
                      <p className="text-xs text-slate-500 mt-2">
                        Accepted formats: .txt, .doc, .docx, .pdf (used for metadata analysis only)
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Generating KDP Metadata...' : 'Generate KDP Submission Guide with Digital Ink AI'}
                    </button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Generation Features */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Generation Features</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span>7 optimized KDP categories</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span>7 strategic keywords</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span>Optimized book description</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span>Enhanced author biography</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span>Back cover copy</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span>Amazon search terms</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span>BISAC category recommendations</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-blue-500 mr-2">✓</span>
                    <span>Marketing copy for ads</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
      ) : (
        // Results Display
          <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
            {/* Sticky Header with Logo and Navigation - Matching BVADM */}
            <header className="bg-white shadow-sm border-b print:hidden sticky top-0 z-50">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <img 
                      src="/Digital_Ink_AI_Logo.png" 
                      alt="Digital Ink AI Logo" 
                      className="h-12 w-auto"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                      }}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate('/homepage')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      🏠 Home
                    </button>
                    <button
                      onClick={() => navigate('/user_dashboard')}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      📊 Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </header>

            {/* Main Content */}
            <main className="max-w-6xl mx-auto px-6 py-8">
              <div className="bg-white rounded-lg shadow-lg p-8">
                {/* Report Metadata - Matching BVADM Style */}
                <div className="border-b pb-6 mb-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">AKSW Analysis Report</h2>
                      <p className="text-slate-600 mt-1">Amazon KDP Submission Guide for {report.book_title}</p>
                      {report.subtitle && (
                        <p className="text-slate-600 text-sm italic">{report.subtitle}</p>
                      )}
                      {report.author_name && (
                        <p className="text-slate-600 text-sm mt-1">by {report.author_name}</p>
                      )}
                    </div>
                    <div className="flex gap-3 print:hidden">
                      <button onClick={printResults} className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">🖨️ Print</button>
                      <button onClick={exportToPDF} className="hidden md:inline-flex px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">📄 Export PDF</button>
                      <button onClick={handleNewReport} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">✨ New Analysis</button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Book Title:</span>
                      <p className="font-semibold text-slate-900">{report.book_title || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Subtitle:</span>
                      <p className="font-semibold text-slate-900">{report.subtitle || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Author Name:</span>
                      <p className="font-semibold text-slate-900">{report.author_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Publisher:</span>
                      <p className="font-semibold text-slate-900">{formData.publisher_name || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Service:</span>
                      <p className="font-semibold text-slate-900">AKSW</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Book Price:</span>
                      <p className="font-semibold text-slate-900">${formData.book_price}</p>
                    </div>
                  </div>
                </div>

            <div id="aksw-results" className="space-y-6">

              {/* KDP Categories */}
              <Card className="border-l-4 border-blue-500">
                <CardHeader>
                  <CardTitle className="text-blue-900">📚 KDP Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {report.kdp_categories?.map((cat, idx) => (
                      <li key={idx}>{cat}</li>
                    )) || <li>No categories</li>}
                  </ul>
                </CardContent>
              </Card>

              {/* KDP Keywords */}
              <Card className="border-l-4 border-purple-500">
                <CardHeader>
                  <CardTitle className="text-purple-900">🔑 KDP Keywords</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {report.kdp_keywords?.map((kw, idx) => (
                      <li key={idx}>{kw}</li>
                    )) || <li>No keywords</li>}
                  </ul>
                </CardContent>
              </Card>

              {/* Book Description */}
              <Card className="border-l-4 border-green-500">
                <CardHeader>
                  <CardTitle className="text-green-900">📝 Book Description</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap">{report.book_description}</p>
                </CardContent>
              </Card>

              {/* Author Bio */}
              <Card className="border-l-4 border-amber-500">
                <CardHeader>
                  <CardTitle className="text-amber-900">👤 Author Bio (Optimized)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap">{report.author_bio_optimized}</p>
                </CardContent>
              </Card>

              {/* Back Cover Copy */}
              <Card className="border-l-4 border-pink-500">
                <CardHeader>
                  <CardTitle className="text-pink-900">📖 Back Cover Copy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap">{report.back_cover_copy}</p>
                </CardContent>
              </Card>

              {/* Search Terms */}
              <Card className="border-l-4 border-cyan-500">
                <CardHeader>
                  <CardTitle className="text-cyan-900">🔍 Amazon Search Terms</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {report.search_terms?.map((term, idx) => (
                      <li key={idx}>{term}</li>
                    )) || <li>No terms</li>}
                  </ul>
                </CardContent>
              </Card>

              {/* Age & Grade */}
              <Card className="border-l-4 border-indigo-500">
                <CardHeader>
                  <CardTitle className="text-indigo-900">🎯 Age Range & Grade Level</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700"><strong>Age Range:</strong> {report.age_range}</p>
                  <p className="text-slate-700 mt-2"><strong>Grade Level:</strong> {report.grade_level}</p>
                </CardContent>
              </Card>

              {/* BISAC Categories */}
              <Card className="border-l-4 border-lime-500">
                <CardHeader>
                  <CardTitle className="text-lime-900">📊 BISAC Categories</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside space-y-2 text-slate-700">
                    {report.bisac_categories?.map((cat, idx) => (
                      <li key={idx}>{cat}</li>
                    )) || <li>No categories</li>}
                  </ul>
                </CardContent>
              </Card>

              {/* Marketing Copy */}
              <Card className="border-l-4 border-red-500">
                <CardHeader>
                  <CardTitle className="text-red-900">🎬 Marketing Copy</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap">{report.marketing_copy}</p>
                </CardContent>
              </Card>
            </div>
              </div>
            </main>
          </div>
        )}

        {/* Payment Validation Modal */}
      <PaymentValidationModal
        isOpen={showPaymentValidation}
        onClose={() => {
          setShowPaymentValidation(false);
          navigate('/user_dashboard');
        }}
        onSuccess={handlePaymentValidationSuccess}
      />

      {/* Service Charge Authorization Modal */}
      <ServiceChargeModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onConfirm={handlePaymentSuccess}
        serviceInfo={paymentInfo}
        user={user}
      />

      {/* Processing Modal */}
      <ProcessingModal 
        isOpen={isProcessing} 
        serviceName="Amazon KDP Submission Wizard (AKSW)"
        bookTitle={formData.book_title}
        progress={processingProgress}
        currentStep={currentStep}
        progressSteps={progressSteps}
      />

      {/* Email Receipt Modal */}
      <EmailReceiptModal
        isOpen={showEmailReceiptModal}
        onClose={() => setShowEmailReceiptModal(false)}
        onSendReceipt={async () => {
          try {
            await axios.post(`${API}/send-receipt-email`, {
              transaction_id: lastTransactionId,
              user_email: user.email
            }, { withCredentials: true });
            toast.success('Receipt sent to your email!');
            setShowEmailReceiptModal(false);
          } catch (error) {
            console.error('Receipt email error:', error);
            toast.error('Failed to send receipt');
          }
        }}
        serviceName="Amazon KDP Submission Wizard (AKSW)"
        amount={lastCharge}
        bookTitle={formData.book_title}
      />

      {/* Service Access Denied */}
      {serviceAccessDenied && !showPaymentValidation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Service Access Required</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 mb-4">
                Please complete payment validation to access this service.
              </p>
              <Button 
                onClick={() => navigate('/user_dashboard')}
                className="w-full"
              >
                Return to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Show form only if payment completed */}
      {!paymentCompleted && !showPaymentValidation && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="animate-pulse">
                  <div className="w-12 h-12 bg-orange-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">Validating Service Access</h3>
                <p className="text-slate-600 mb-4">Please wait while we verify your payment information...</p>
                <Button 
                  onClick={validateServiceAccess}
                  variant="outline"
                  className="mt-4"
                >
                  Retry Validation
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AKSWPage;
